import * as React from 'react';
import { ArrowRight } from 'lucide-react';

import { Badge, type BadgeProps } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { cardVariants } from 'components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { cn } from 'lib/utils';

/**
 * Pieza NUEVA (no es puerto de chirigota) — la card de una adhesión/plan
 * dentro de la pantalla de aterrizaje Multicuenta (Resumen), wireframe 05
 * "Usuario con varios planes" (5A y 5B). Puramente visual: como
 * `ui/TransferStatusCard`, no conoce `PlanSummary`/`PlanStatus` ni ningún
 * tipo de chirigota — quien la use ya resuelve badge/tono/textos antes de
 * pasarlos (mismo criterio que el resto del puerto). El logo real de la
 * entidad gestora (`PlanLogo` en chirigota, con sus assets reales por id de
 * plan) tampoco vive aquí: se pasa como `icon` (ver `ui/PlanLogo`).
 *
 * `statusLine` cubre las 3 variantes mutuamente excluyentes ya vistas en los
 * wireframes bajo el importe: aportación mensual normal, aviso de Suspenso
 * ("debes traspasar el plan"), o aviso de bloqueo por identidad ("no podrás
 * aportar...") — nunca las tres a la vez, por eso es una única línea con tono.
 *
 * `children` es un slot aparte, DEBAJO de `statusLine`: para lo que sí puede
 * convivir con la aportación normal, como el widget de `ui/TransferStatusCard`
 * + `ui/TransferProgressSummary` (traspaso en curso/completado/en espera) o
 * una nota corta tipo "Operativo sin KYC" — ver wireframes 5A/5B, donde varias
 * cards muestran aportación Y el bloque de traspaso a la vez.
 */
export interface PlanCardBadge {
	label: string;
	variant?: BadgeProps['variant'];
}

export interface PlanCardStatusLine {
	text: React.ReactNode;
	tone?: 'default' | 'danger';
}

export interface PlanCardProps {
	icon: React.ReactNode;
	name: string;
	/** `id` del nombre en el DOM — para que quien use esto pueda distinguir dos cards con el mismo `name` (ej. dos "Plan de empleo", uno activo y uno suspenso de una empresa anterior). */
	nameId?: string;
	subtitle?: string;
	badge: PlanCardBadge;
	/** Encima del importe grande — qué es ese número (ej. "Total ahorrado"). Por defecto, ninguna (compat). */
	valueLabel?: string;
	/** Sin esto (plan nuevo, aún sin cifras — ej. Pendiente KYC) no se pinta ni esto ni la rentabilidad: un "0,00 €" no aporta nada y parece un error. */
	value?: string;
	/** Antes de `rentabilidadLabel` — por defecto "Rentabilidad"; pasar con el año (ej. "Rentabilidad 2026") para dejar claro de qué periodo es. */
	rentabilidadCaption?: string;
	/** Versión abreviada de `valueLabel` para la card condensada (ej. "Ahorrado"). El texto completo se queda para lectores de pantalla. */
	valueLabelShort?: string;
	/** Versión abreviada de `rentabilidadCaption` para la card condensada (ej. "Rent. 2026"). El texto completo se queda para lectores de pantalla. */
	rentabilidadCaptionShort?: string;
	rentabilidadLabel?: string;
	statusLine?: PlanCardStatusLine;
	/** Widget embebido opcional (traspaso, nota de KYC...), debajo de `statusLine`. */
	children?: React.ReactNode;
	ctaLabel?: string;
	onCtaClick?: () => void;
	/** Atenúa importe y rentabilidad (plan en solo lectura por identidad sin verificar). El acceso al detalle NUNCA se bloquea — solo cambia `ctaLabel` si quien lo use quiere reflejarlo (ej. "Ver detalle (solo lectura)"). */
	readOnly?: boolean;
	/**
	 * Versión corta para móvil: nombre, cifras más pequeñas (con sus labels), una única línea de
	 * estado (`condensedStatus`) y el CTA. Quita el badge de tipo, el subtítulo, `statusLine` y el
	 * widget (`children`), para que las cards de un slider (`ui/CardSlider`) midan todas lo mismo y
	 * no haya saltos de altura.
	 *
	 * - `'auto'` (por defecto): condensada por debajo de `lg` (1024 px, el mismo corte que el
	 *   slider de Resumen) y completa desde `lg`, en CSS puro, sin JS. `display: none` también
	 *   lo esconde a los lectores de pantalla.
	 * - `true`: siempre condensada (dentro de un `CardSlider`).
	 * - `false`: nunca.
	 */
	condensed?: boolean | 'auto';
	/**
	 * Solo con `condensed`: el estado de la card en UNA frase corta ("Traspaso en curso",
	 * "Verifica tu identidad para aportar"). Quien usa esto elige qué frase manda (p. ej. la
	 * operativa en curso sobre la aportación mensual). Si falta, se usa `statusLine`.
	 */
	condensedStatus?: PlanCardStatusLine;
	className?: string;
}

export const PlanCard = ({
	icon,
	name,
	nameId,
	subtitle,
	badge,
	valueLabel,
	valueLabelShort,
	value,
	rentabilidadCaption = 'Rentabilidad',
	rentabilidadCaptionShort,
	rentabilidadLabel,
	statusLine,
	children,
	ctaLabel = 'Ver detalle',
	onCtaClick,
	readOnly = false,
	condensed = 'auto',
	condensedStatus,
	className
}: PlanCardProps) => {
	// Clases literales (Tailwind no ve las que se construyen por partes).
	const ocultaCondensada =
		condensed === true ? 'hidden' : condensed === 'auto' ? 'max-lg:hidden' : '';
	const soloCondensada = condensed === true ? '' : condensed === 'auto' ? 'lg:hidden' : 'hidden';
	// El label completo pasa a `sr-only` cuando se muestra el corto: una abreviatura («Rent.») no
	// debe ser lo que lea un lector de pantalla.
	const completoSiHayCorto =
		condensed === true ? 'sr-only' : condensed === 'auto' ? 'max-lg:sr-only' : '';
	const etiqueta = (completo: string, corto?: string) =>
		corto ? (
			<>
				<span aria-hidden="true" className={soloCondensada}>
					{corto}
				</span>
				<span className={completoSiHayCorto}>{completo}</span>
			</>
		) : (
			completo
		);
	const cifraCondensada =
		condensed === true
			? 'text-lg'
			: condensed === 'auto'
				? 'text-lg lg:text-[22px]'
				: 'text-[22px]';

	return (
		<article
			className={cn(cardVariants({ variant: 'elevated' }), 'p-4 flex flex-col gap-3', className)}
			aria-label={name}
		>
			<div className="flex items-start justify-between gap-2">
				<div className="flex min-w-0 items-center gap-2">
					{icon}
					<span className="min-w-0 flex-1">
						{/* El nombre se recorta con «…» y el tooltip del sistema lo muestra entero al pasar el ratón
					    por encima. `Tooltip` y no `ResponsiveTooltip`: en móvil éste lo envolvía en un Popover
					    cuyo disparador, aquí un <span>, recibía `aria-expanded`/`aria-haspopup`, que no son válidos en
					    un span (axe: aria-allowed-attr). En táctil no hay hover, así que el nombre completo queda en
					    el `aria-label` de la card y en el detalle del plan. */}
						<TooltipProvider delayDuration={100}>
							<Tooltip>
								<TooltipTrigger asChild>
									<span id={nameId} className="block truncate text-sm font-heading">
										{name}
									</span>
								</TooltipTrigger>
								<TooltipContent side="top" className="max-w-xs text-xs">
									{name}
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
						{subtitle && (
							<span
								className={cn('block truncate text-xs text-muted-foreground', ocultaCondensada)}
							>
								{subtitle}
							</span>
						)}
					</span>
				</div>
				<Badge
					variant={badge.variant ?? 'outline'}
					className={cn('shrink-0 whitespace-nowrap', ocultaCondensada)}
				>
					{badge.label}
				</Badge>
			</div>

			{value !== undefined && (
				<div className="flex items-start justify-between gap-2">
					<div>
						{valueLabel && (
							<p className="text-xs text-muted-foreground">
								{etiqueta(valueLabel, valueLabelShort)}
							</p>
						)}
						<p className={cn(cifraCondensada, 'font-medium', readOnly && 'text-muted-foreground')}>
							{value}
						</p>
					</div>
					{rentabilidadLabel && (
						<div className="shrink-0 text-right">
							{rentabilidadCaption && (
								<p className="text-xs text-muted-foreground">
									{etiqueta(rentabilidadCaption, rentabilidadCaptionShort)}
								</p>
							)}
							<p
								className={cn(
									cifraCondensada,
									'font-medium',
									readOnly
										? 'text-muted-foreground'
										: rentabilidadLabel.trim().startsWith('-')
											? 'text-warning-text'
											: 'text-primary-text'
								)}
							>
								{rentabilidadLabel}
							</p>
						</div>
					)}
				</div>
			)}

			{statusLine && (
				<p
					className={cn(
						'text-xs',
						ocultaCondensada,
						// text-destructive-text, no text-destructive: bug real de axe en oscuro
						// (3,52:1) — mismo motivo que --warning-text/--primary-text, --destructive
						// no se mueve entre temas pero --destructive-text sí (pink-59 claro →
						// pink-73 oscuro, ya pensado para esto).
						statusLine.tone === 'danger' ? 'text-destructive-text' : 'text-muted-foreground'
					)}
				>
					{statusLine.text}
				</p>
			)}

			{/* La frase corta; sin `condensedStatus`, el `statusLine` (ya es una línea). */}
			{(condensedStatus ?? statusLine) && condensed !== false && (
				<p
					className={cn(
						'text-xs',
						soloCondensada,
						(condensedStatus ?? statusLine)?.tone === 'danger'
							? 'text-destructive-text'
							: 'text-muted-foreground'
					)}
				>
					{(condensedStatus ?? statusLine)?.text}
				</p>
			)}

			{children && <div className={ocultaCondensada || undefined}>{children}</div>}

			<Button variant="outline" size="sm" className="mt-auto w-full" onClick={onCtaClick}>
				{ctaLabel}
				<ArrowRight className="h-4 w-4" aria-hidden="true" />
			</Button>
		</article>
	);
};
